/**
 * QMS CEO Review Panel
 * Panel for CEO review actions on high-priority tickets
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, CheckCircle, TrendingUp, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { useCeoReview } from '../../hooks/useQmsTicket';
import { useQmsPermissions } from '../../hooks/useQmsPermissions';
import type { QmsTicket, QmsRiskLevel, QmsPriority } from '../../types/qms.types';
import { isPopulatedUser } from '../../types/qms.types';

interface QmsCeoReviewPanelProps {
  ticket: QmsTicket;
  onReviewComplete: () => void;
}

export function QmsCeoReviewPanel({ ticket, onReviewComplete }: QmsCeoReviewPanelProps) {
  const { t } = useTranslation('qms');
  const { canActAsCEO } = useQmsPermissions();
  const ceoReviewMutation = useCeoReview();

  const [showReviewForm, setShowReviewForm] = useState(false);
  const [action, setAction] = useState<'confirm' | 'adjust_priority'>('confirm');
  const [notes, setNotes] = useState('');
  const [riskLevel, setRiskLevel] = useState<QmsRiskLevel>(ticket.risk_level || 'medium');
  const [newPriority, setNewPriority] = useState<QmsPriority>(ticket.priority);
  const [actionError, setActionError] = useState<string | null>(null);

  const isPending = ticket.requires_ceo_review && ticket.ceo_review_status === 'pending';
  const isConfirmed = ticket.ceo_review_status === 'confirmed';
  const isPriorityAdjusted = ticket.ceo_review_status === 'priority_adjusted';

  const handleSubmit = async () => {
    setActionError(null);

    try {
      await ceoReviewMutation.mutateAsync({
        ticketId: ticket._id!,
        payload: {
          action,
          notes,
          risk_level: riskLevel,
          new_priority: action === 'adjust_priority' ? newPriority : undefined,
        },
      });
      setShowReviewForm(false);
      setNotes('');
      onReviewComplete();
    } catch (error) {
      console.error('Failed to submit CEO review:', error);
      setActionError(t('errors.unexpected'));
    }
  };

  // Only show if ticket requires CEO review
  if (!ticket.requires_ceo_review) {
    return null;
  }

  const getRiskBadgeColor = (risk: QmsRiskLevel) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800',
    };
    return colors[risk] || colors.medium;
  };

  return (
    <div className="border-t pt-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('ceoReview.title')}</h3>

      {/* Current Status */}
      <div className="bg-purple-50 rounded-lg p-4 mb-4 border border-purple-200">
        <div className="flex items-center gap-3">
          {/* Status Icon */}
          {isPending && (
            <>
              <Shield className="h-5 w-5 text-purple-600" />
              <div>
                <p className="font-medium text-gray-900">{t('ceoReview.pendingTitle')}</p>
                <p className="text-sm text-gray-600">{t('ceoReview.pendingDescription')}</p>
              </div>
            </>
          )}

          {isConfirmed && (
            <>
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium text-gray-900">{t('ceoReview.confirmedTitle')}</p>
                {isPopulatedUser(ticket.ceo_reviewed_by) && (
                  <p className="text-sm text-gray-600">
                    {ticket.ceo_reviewed_by.firstname} {ticket.ceo_reviewed_by.lastname}
                    {ticket.ceo_reviewed_at && (
                      <> • {format(new Date(ticket.ceo_reviewed_at), 'MMM d, yyyy')}</>
                    )}
                  </p>
                )}
              </div>
            </>
          )}

          {isPriorityAdjusted && (
            <>
              <TrendingUp className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium text-gray-900">{t('ceoReview.priorityAdjustedTitle')}</p>
                {isPopulatedUser(ticket.ceo_reviewed_by) && (
                  <p className="text-sm text-gray-600">
                    {ticket.ceo_reviewed_by.firstname} {ticket.ceo_reviewed_by.lastname}
                    {ticket.ceo_reviewed_at && (
                      <> • {format(new Date(ticket.ceo_reviewed_at), 'MMM d, yyyy')}</>
                    )}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Risk Level Badge */}
        {ticket.risk_level && (
          <div className="mt-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-gray-600" />
            <span className="text-sm text-gray-600">{t('ceoReview.riskLevelLabel')}</span>
            <span
              className={`px-2 py-0.5 rounded text-xs font-medium ${getRiskBadgeColor(
                ticket.risk_level
              )}`}
            >
              {t(`ceoReview.riskLevels.${ticket.risk_level}`)}
            </span>
          </div>
        )}

        {/* Review Notes */}
        {ticket.ceo_review_notes && (
          <div className="mt-3 pt-3 border-t border-purple-200">
            <p className="text-sm font-medium text-gray-700 mb-1">{t('ceoReview.notesLabel')}</p>
            <p className="text-sm text-gray-600">{ticket.ceo_review_notes}</p>
          </div>
        )}
      </div>

      {/* Action Buttons (only if user can act as CEO and ticket is pending) */}
      {canActAsCEO && isPending && !showReviewForm && (
        <div className="flex gap-2">
          <button
            onClick={() => {
              setAction('confirm');
              setShowReviewForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <CheckCircle className="h-4 w-4" />
            {t('ceoReview.actions.confirm')}
          </button>

          <button
            onClick={() => {
              setAction('adjust_priority');
              setShowReviewForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <TrendingUp className="h-4 w-4" />
            {t('ceoReview.actions.adjustPriority')}
          </button>
        </div>
      )}

      {/* Review Form */}
      {showReviewForm && (
        <div className="bg-purple-50 rounded-lg p-4 space-y-4">
          {/* Risk Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('ceoReview.form.riskAssessment')}
            </label>
            <div className="grid grid-cols-4 gap-2">
              {(['low', 'medium', 'high', 'critical'] as QmsRiskLevel[]).map((risk) => (
                <button
                  key={risk}
                  type="button"
                  onClick={() => setRiskLevel(risk)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    riskLevel === risk
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t(`ceoReview.riskLevels.${risk}`)}
                </button>
              ))}
            </div>
          </div>

          {/* New Priority (only for adjust_priority action) */}
          {action === 'adjust_priority' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('ceoReview.form.newPriority')}
              </label>
              <select
                value={newPriority}
                onChange={(e) => setNewPriority(parseInt(e.target.value) as QmsPriority)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
              >
                <option value={1}>{t('priority.1')}</option>
                <option value={2}>{t('priority.2')}</option>
                <option value={3}>{t('priority.3')}</option>
                <option value={4}>{t('priority.4')}</option>
                <option value={5}>{t('priority.5')}</option>
              </select>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('ceoReview.form.reviewNotes')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
              placeholder={t('ceoReview.form.placeholder')}
            />
          </div>

          {/* Error display */}
          {actionError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{actionError}</p>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => {
                setShowReviewForm(false);
                setNotes('');
                setActionError(null);
              }}
              disabled={ceoReviewMutation.isPending}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              {t('actions.cancel')}
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={ceoReviewMutation.isPending}
              className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${
                action === 'confirm'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {ceoReviewMutation.isPending ? t('actions.submitting') : t('actions.submit')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
